import { nonPageTracker, useAdobeDataLayer } from '@src/adobe-launch/data-layer-provider';
import {
    InteractionInfo,
    PageViewParam,
    PrevewPageViewParam,
    PreviewTrackedArticleInfo,
    TrackedArticleInfo,
} from '@src/adobe-launch/global';
import { AppLanguage } from '@src/common/app-language';
import { DiscoverQueryParams } from '@src/common/enums';
import { AppArticlePageProp } from '@src/common/types';
import { getSearchParams } from '@src/utils/query-string';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useWAConfig } from '../api/use-wa-config';
import { useGlobalStore } from '../use-global-store';
import { convertArticleInfo, previewPageConvertArticleInfo } from './use-article-tracker';
import { useEU } from './use-eu';
//import Script from 'next/script';

interface TrackerFunc {
    pageTracker: (
        lang: string | undefined,
        article?: TrackedArticleInfo | PreviewTrackedArticleInfo | any
    ) => void;
    nonPageTracker: (interaction: InteractionInfo | any) => void;
}
export const TrackerFuncContext = createContext<TrackerFunc | null>(null);

export const TrackerProvider = observer((props: { children?: ReactNode }) => {
    const adobeDataLayer = useAdobeDataLayer();
    const isEU = useEU();
    const wa = useWAConfig();
    const [trackers, setTrackers] = useState<TrackerFunc | null>(null);
    const { settingStore } = useGlobalStore();
    const router = useRouter();
    const [watrackingFired, setWatrackingFired] = useState(false);

    const isPreviewPage = /\/share\//.test(router.asPath);
    // const isPreviewPage = router.asPath.startsWith('/share/');
    // console.log('adobeDataLayer====', adobeDataLayer);

    useEffect(() => {
        if (!watrackingFired && adobeDataLayer && isEU !== undefined && wa && isPreviewPage) {
            adobeDataLayer.push({
                event: 'watracking',
                IsEU: isEU,
            });
            setWatrackingFired(true);
        }

        if (!watrackingFired && adobeDataLayer && isEU !== undefined && wa && !isPreviewPage) {
            adobeDataLayer.push({
                event: 'watracking',
                IsEU: isEU ? 'yes' : 'no',
                masterSwitch: wa.waConfig?.masterSwitch?.boolValue ? 'on' : 'off',
                reversedProxy: wa.waConfig?.reversedProxy?.boolValue ? 'on' : 'off',
            });
            setWatrackingFired(true);
        }
    }, [adobeDataLayer, isEU, isPreviewPage, wa, watrackingFired]);

    useEffect(() => {
        if (
            watrackingFired &&
            adobeDataLayer &&
            isEU !== undefined &&
            wa &&
            !settingStore.isLoading &&
            !isPreviewPage
        ) {
            const searchParams = getSearchParams();
            const ac = searchParams.getAll(DiscoverQueryParams.account).join(',');
            const seg = searchParams.getAll(DiscoverQueryParams.segment).join(',');
            const user: any = {
                account_list_id: ac,
                segment_code: seg,
                cuno: settingStore.wid || '',
            };

            const pageTracker = (lang: string | undefined, article?: TrackedArticleInfo) => {
                const param: PageViewParam = {
                    event: 'pageview',
                    user,
                    page: {
                        language: lang || AppLanguage.en_us,
                        name: '',
                        type: article ? 'article' : '',
                    },
                    article,
                };
                adobeDataLayer.push(param);
            };
            setTrackers({ pageTracker, nonPageTracker });
        }
    }, [
        adobeDataLayer,
        isEU,
        isPreviewPage,
        settingStore.isLoading,
        settingStore.wid,
        wa,
        watrackingFired,
    ]);

    useEffect(() => {
        if (
            watrackingFired &&
            adobeDataLayer &&
            isEU !== undefined &&
            wa &&
            !settingStore.isLoading &&
            isPreviewPage
        ) {
            const pageTracker = (lang: string | undefined, article?: PreviewTrackedArticleInfo) => {
                // console.log('lang----', lang);
                const param: PrevewPageViewParam = {
                    event: 'pageview',
                    page: {
                        name: '',
                        id: 'o2oDiscoverPreview',
                        language: lang || AppLanguage.en_us,
                    },
                    ...article,
                };
                adobeDataLayer.push(param);
            };
            setTrackers({ pageTracker, nonPageTracker });
        }
    }, [
        adobeDataLayer,
        isEU,
        isPreviewPage,
        settingStore.isLoading,
        settingStore.wid,
        wa,
        watrackingFired,
    ]);

    return (
        <TrackerFuncContext.Provider value={trackers}>
            {/* {isEU === false && (
                <Script
                    src={process.env.NEXT_PUBLIC_GLASSBOX_REPORTER_URL}
                    type='text/javascript'
                    id='_cls_detector'
                    data-clsconfig='reportURI=https://report.hkjc.glassboxdigital.io/thickclient/report/4590ACCA-1C63-EA1B-867D-5D1F47E5BF42/cls_report'
                />
            )} */}
            {props.children}
        </TrackerFuncContext.Provider>
    );
});

/**
 *set up trackers
 *
 */
export function usePageTrackers(pageProps: any) {
    // console.log('pageProps==========', pageProps);
    const ctx = useContext(TrackerFuncContext);
    const router = useRouter();
    const isPreviewPage = /\/share\//.test(router.asPath);
    useEffect(() => {
        if (isPreviewPage) {
            // console.log('*****************pageTracker************', pageProps?.pageType);
            if (ctx) {
                switch ((pageProps as AppArticlePageProp).pageType) {
                    case 'article':
                        ctx.pageTracker(
                            router.locale === 'en-US' ? 'en' : router.locale,
                            previewPageConvertArticleInfo(
                                (pageProps as AppArticlePageProp)?.sectionData?.Item
                            )
                        );
                        break;
                    case 'detailArticle':
                        ctx.pageTracker(
                            router.locale === 'en-US' ? 'en' : router.locale,
                            previewPageConvertArticleInfo(
                                (pageProps as AppArticlePageProp)?.contentBlockData
                            )
                        );
                        break;

                    default:
                        if (pageProps.contentBlockData) {
                            return ctx.pageTracker(
                                router.locale === 'en-US' ? 'en' : router.locale,
                                previewPageConvertArticleInfo(
                                    (pageProps as AppArticlePageProp)?.contentBlockData
                                )
                            );
                        }
                        ctx.pageTracker(router.locale === 'en-US' ? 'en' : router.locale);
                        break;
                }
            }
            return;
        }

        if (ctx) {
            switch ((pageProps as AppArticlePageProp).pageType) {
                case 'article':
                    ctx.pageTracker(
                        router.locale,
                        convertArticleInfo((pageProps as AppArticlePageProp)?.sectionData?.Item)
                    );
                    break;
                case 'detailArticle':
                    ctx.pageTracker(
                        router.locale,
                        convertArticleInfo((pageProps as AppArticlePageProp)?.contentBlockData)
                    );
                    break;

                default:
                    ctx.pageTracker(router.locale);
                    break;
            }
        }
    }, [ctx, isPreviewPage, pageProps, router.locale]);
}
